import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useVersions } from '@/features/versions/api';
import { Card } from '@/components/ui/Card';
import type { AdminVersionRow } from '@honeycomb/shared';

/** Format an epoch-ms timestamp to "YYYY-MM-DD HH:MM" (UTC), matching the API label. */
function formatReleased(epochMs: number): string {
  const d = new Date(epochMs);
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const min = String(d.getUTCMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
}

/**
 * Monospace cell that shows the first 7 chars of a long identifier and copies
 * the full string to the clipboard on click.
 */
function CopyableId({ value, testId }: { value: string; testId: string }) {
  const [copied, setCopied] = useState(false);

  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      // Clipboard unavailable (e.g. insecure context) — no-op.
    }
  };

  const truncated = value.length > 7 ? value.slice(0, 7) : value;

  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      title={`${value} (click to copy)`}
      className="font-mono text-xs text-ink-soft hover:text-ink cursor-pointer"
    >
      {copied ? 'Copied!' : truncated}
    </button>
  );
}

export const Route = createFileRoute('/_authed/versions')({
  component: VersionsPage,
});

function VersionsPage() {
  const { data, isLoading, isError } = useVersions();

  const rows = data?.rows ?? [];
  const totals = data?.totals ?? { total_users_global: 0, by_platform: { ios: 0, android: 0 } };

  const iosRows = rows.filter((r) => r.platform === 'ios');
  const androidRows = rows.filter((r) => r.platform === 'android');

  return (
    <div data-testid="versions-page" className="p-4 sm:p-6 lg:p-8 flex flex-col gap-6">
      <header className="mb-2">
        <h1 className="font-serif text-3xl leading-tight">App versions</h1>
        <p
          data-testid="versions-active-window"
          className="text-ink-soft text-sm mt-1"
        >
          Last 7 days
        </p>
      </header>

      {isError && (
        <Card shadow="sm" className="p-4 text-sm text-ink-soft" data-testid="versions-error">
          Failed to load version metrics. Please retry.
        </Card>
      )}

      {!isError && <>
      {/* Global total */}
      <Card shadow="sm" className="p-4 sm:p-6 flex items-center gap-6">
        <div>
          <div className="text-xs uppercase tracking-wide text-ink-faint mb-1">
            Active users (global, native)
          </div>
          <div
            data-testid="versions-global-active-total"
            className="font-mono text-2xl tabular-nums"
          >
            {isLoading ? '…' : totals.total_users_global}
          </div>
        </div>
        <div className="flex gap-6 text-sm">
          <div>
            <span className="text-ink-faint mr-1">iOS:</span>
            <span className="font-mono tabular-nums">{isLoading ? '…' : totals.by_platform.ios}</span>
          </div>
          <div>
            <span className="text-ink-faint mr-1">Android:</span>
            <span className="font-mono tabular-nums">{isLoading ? '…' : totals.by_platform.android}</span>
          </div>
        </div>
      </Card>

      {isLoading ? (
        <div className="text-ink-soft text-sm">Loading…</div>
      ) : (
        <>
          <PlatformSection
            platform="ios"
            label="iOS"
            rows={iosRows}
            totalUsers={totals.by_platform.ios}
          />
          <PlatformSection
            platform="android"
            label="Android"
            rows={androidRows}
            totalUsers={totals.by_platform.android}
          />
        </>
      )}
      </>}
    </div>
  );
}

function PlatformSection({
  platform,
  label,
  rows,
  totalUsers,
}: {
  platform: 'ios' | 'android';
  label: string;
  rows: AdminVersionRow[];
  totalUsers: number;
}) {
  return (
    <section data-testid={`versions-platform-section-${platform}`}>
      <h2 className="font-serif text-xl mb-3">
        {label}{' '}
        <span className="font-sans text-sm text-ink-faint font-normal">
          ({totalUsers} active user{totalUsers !== 1 ? 's' : ''})
        </span>
      </h2>

      {rows.length === 0 ? (
        <Card shadow="sm" className="p-4 text-ink-faint text-sm">
          No active sessions in the last 7 days.
        </Card>
      ) : (
        <Card shadow="sm" className="p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr
                className="text-left text-xs uppercase tracking-wide text-ink-faint border-b"
                style={{ borderColor: 'var(--color-hairline)' }}
              >
                <th className="px-4 py-3">Version</th>
                <th className="px-4 py-3">Released</th>
                <th className="px-4 py-3">Runtime version</th>
                <th className="px-4 py-3">OTA update ID</th>
                <th className="px-4 py-3 text-right">Users</th>
                <th className="px-4 py-3 text-right">% of platform</th>
                <th className="px-4 py-3 text-right">% of all native</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const otaKey = row.ota_update_id ?? 'embedded';
                const testId = `versions-row-${row.platform}-${row.runtime_version}-${otaKey}`;
                return (
                  <tr
                    key={testId}
                    data-testid={testId}
                    className="border-b last:border-b-0 hover:bg-cream-deep/60"
                    style={{ borderColor: 'var(--color-hairline)' }}
                  >
                    <td className="px-4 py-3">
                      <span
                        data-testid="versions-row-label"
                        className="text-sm font-medium"
                      >
                        {row.label}
                      </span>
                    </td>
                    <td
                      data-testid="versions-row-released"
                      className="px-4 py-3 font-mono text-xs text-ink-soft whitespace-nowrap"
                    >
                      {row.published_at != null ? (
                        formatReleased(row.published_at)
                      ) : (
                        <span className="text-ink-faint">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <CopyableId
                        value={row.runtime_version}
                        testId="versions-row-runtime"
                      />
                    </td>
                    <td className="px-4 py-3">
                      {row.ota_update_id ? (
                        <CopyableId
                          value={row.ota_update_id}
                          testId="versions-row-ota"
                        />
                      ) : (
                        <span
                          data-testid="versions-row-ota"
                          className="font-mono text-xs text-ink-faint italic"
                        >
                          embedded
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono tabular-nums text-right">
                      {row.users_on_version}
                    </td>
                    <td className="px-4 py-3 font-mono tabular-nums text-right">
                      {row.percent_of_platform}%
                    </td>
                    <td className="px-4 py-3 font-mono tabular-nums text-right">
                      {row.percent_of_global}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </section>
  );
}
